import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import "./Chart.css";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function Chart({ title }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [algo1Data, setAlgo1Data] = useState([]);
  const API_URL = process.env.REACT_APP_API_FAST || "http://192.168.1.103:8000";

  /* =========================================================
     FETCH DATA
  ========================================================= */

  useEffect(() => {
    const fetchAlgorithmData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_URL}/pastresult/${title}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        const receivedData = Array.isArray(data?.[title]) ? data[title] : [];

        setAlgo1Data(receivedData);
      } catch (err) {
        console.error("Chart fetch error:", err);

        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (title) {
      fetchAlgorithmData();
    }
  }, [title]);

  /* =========================================================
     HELPERS
  ========================================================= */

  const formatDate = (dateString) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return String(dateString);
    }

    return date.toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
    });
  };
  const getPointColor = (percentage) => {
    if (percentage >= 90) {
      return "#15803d";
    }

    if (percentage >= 75) {
      return "#16a34a";
    }

    if (percentage >= 50) {
      return "#ca8a04";
    }

    return "#dc2626";
  };
  /* =========================================================
     CLEAN + SORT DATA
  ========================================================= */

  const chartRows = useMemo(() => {
    return [...algo1Data]
      .filter((item) => item)
      .sort((a, b) => new Date(a.date_derieved) - new Date(b.date_derieved));
  }, [algo1Data]);

  /* =========================================================
     VALUES
  ========================================================= */

  const labels = chartRows.map((item) => formatDate(item.date_derieved));

  const dataValues = chartRows.map((item) => {
    const value = Number(item.percentage);

    return Number.isFinite(value) ? value : 0;
  });

  const pointColors = dataValues.map((percentage) => getPointColor(percentage));

  /* =========================================================
     STATISTICS
  ========================================================= */

  const latestValue = dataValues[dataValues.length - 1] ?? 0;

  const averageValue =
    dataValues.length > 0
      ? dataValues.reduce((sum, value) => sum + value, 0) / dataValues.length
      : 0;

  const bestValue = dataValues.length > 0 ? Math.max(...dataValues) : 0;

  const totalDays = dataValues.length;

  /* =========================================================
     CHART DATA
  ========================================================= */
  const data = {
    labels,

    datasets: [
      {
        label: "Performance",

        data: dataValues,

        // MAIN LINE
        borderColor: "#334155",
        borderWidth: 2.5,

        tension: 0.35,

        fill: true,

        spanGaps: true,

        // POINTS
        pointRadius: 4,

        pointHoverRadius: 7,

        pointBackgroundColor: pointColors,

        pointBorderColor: "#ffffff",

        pointBorderWidth: 2,

        pointHoverBorderWidth: 2,

        // AREA UNDER LINE
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return "rgba(51, 65, 85, 0.05)";
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );

          gradient.addColorStop(0, "rgba(51, 65, 85, 0.14)");

          gradient.addColorStop(0.55, "rgba(51, 65, 85, 0.05)");

          gradient.addColorStop(1, "rgba(51, 65, 85, 0.00)");

          return gradient;
        },
      },
    ],
  };
  /* =========================================================
     CHART OPTIONS
  ========================================================= */
  const options = {
    responsive: true,

    maintainAspectRatio: false,

    interaction: {
      mode: "index",
      intersect: false,
    },

    layout: {
      padding: {
        top: 15,
        right: 18,
        bottom: 10,
        left: 10,
      },
    },

    plugins: {
      legend: {
        display: false,
      },

      title: {
        display: false,
      },

      tooltip: {
        enabled: true,

        backgroundColor: "#1e293b",

        titleColor: "#ffffff",

        bodyColor: "#e2e8f0",

        borderColor: "#475569",

        borderWidth: 1,

        padding: 12,

        cornerRadius: 8,

        displayColors: false,

        titleFont: {
          size: 12,
          weight: "600",
        },

        bodyFont: {
          size: 11,
          weight: "500",
        },

        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0]?.dataIndex;

            return `Date: ${formatDate(chartRows[index]?.date_derieved)}`;
          },

          label: (context) => {
            const index = context.dataIndex;

            const item = chartRows[index];

            return [
              `Performance: ${item?.percentage ?? 0}%`,
              `Matches: ${item?.total_count ?? 0}`,
              `Correct: ${item?.correct_count ?? 0}`,
            ];
          },
        },
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
        },

        border: {
          display: false,
        },

        ticks: {
          color: "#475569",

          maxRotation: 0,

          minRotation: 0,

          autoSkip: false,

          maxTicksLimit: 8,

          padding: 8,

          font: {
            size: 10,
            weight: "500",
          },
        },
      },

      y: {
        beginAtZero: true,

        max: 100,

        grace: "5%",

        border: {
          display: false,
        },

        ticks: {
          autoSkip: false,
          stepSize: 10,

          color: "#475569",

          padding: 4,

          callback: (value) => `${value}%`,

          font: {
            size: 10,
            weight: "500",
          },
        },

        grid: {
          color: "rgba(100, 116, 139, 0.14)",

          drawTicks: false,

          borderDash: [4, 4],

          lineWidth: 1,
        },
      },
    },

    animation: {
      duration: 650,

      easing: "easeOutQuart",
    },

    hover: {
      mode: "index",

      intersect: false,
    },
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="chart_card chart_loading_state">
        <div className="chart_loading_header">
          <div>
            <div className="chart_skeleton_eyebrow"></div>

            <div className="chart_skeleton_title"></div>

            <div className="chart_skeleton_subtitle"></div>
          </div>

          <div className="chart_skeleton_stat"></div>
        </div>

        <div className="chart_skeleton_legend"></div>

        <div className="chart_skeleton_graph">
          <div className="chart_skeleton_line line_1"></div>

          <div className="chart_skeleton_line line_2"></div>

          <div className="chart_skeleton_line line_3"></div>

          <div className="chart_skeleton_line line_4"></div>
        </div>
      </div>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <div className="chart_card">
        <div className="chart_error">
          <div className="chart_error_icon">!</div>

          <div>
            <strong>Unable to load chart</strong>

            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  /* =========================================================
     EMPTY
  ========================================================= */

  if (!chartRows.length) {
    return (
      <div className="chart_card">
        <div className="chart_empty">
          <div className="chart_empty_icon">∅</div>

          <strong>No performance data</strong>

          <span>There is currently no daily data available.</span>
        </div>
      </div>
    );
  }

  /* =========================================================
     MAIN CHART
  ========================================================= */

  return (
    <div className="chart_card">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="chart_header">
        <div className="chart_title_section">
          <span className="chart_eyebrow">DAILY PERFORMANCE</span>

          <h5>{title}</h5>

          <p>Performance history over time</p>
        </div>

        <div className="chart_latest">
          <span>Latest</span>

          <strong>{latestValue.toFixed(1)}%</strong>
        </div>
      </div>

      {/* =====================================================
          STATISTICS
      ===================================================== */}

      <div className="chart_stats">
        <div className="chart_stat">
          <span>Average</span>

          <strong>{averageValue.toFixed(1)}%</strong>
        </div>

        <div className="chart_stat">
          <span>Best</span>

          <strong>{bestValue.toFixed(1)}%</strong>
        </div>

        <div className="chart_stat">
          <span>Days</span>

          <strong>{totalDays}</strong>
        </div>
      </div>

      {/* =====================================================
          LEGEND
      ===================================================== */}

      <div className="chart_legend_custom">
        <div className="chart_legend_item">
          <span className="legend_dot excellent"></span>

          <span>90%+</span>
        </div>

        <div className="chart_legend_item">
          <span className="legend_dot good"></span>

          <span>75–89%</span>
        </div>

        <div className="chart_legend_item">
          <span className="legend_dot average"></span>

          <span>50–74%</span>
        </div>

        <div className="chart_legend_item">
          <span className="legend_dot weak"></span>

          <span>&lt;50%</span>
        </div>
      </div>

      {/* =====================================================
          CHART
      ===================================================== */}

      <div className="chart_body">
        <Line data={data} options={options} />
      </div>
    </div>
  );
}
